import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { BsPencil } from 'react-icons/bs';
import { FiTrash2 } from 'react-icons/fi';

import AsyncButton from 'modules/common/components/AsyncButton';
import ThumbIcon from 'partial/components/ThumbIcon';
import { useCreateModal } from 'partial/components/Modal';
import { aclHelper } from 'helper';
import AddUpdateRole from '../modals/AddUpdateRole';

import DeleteRole from '../modals/DeleteRole';
import Permissions from './Permissions';

import { useRoleById } from '../hooks';

function RoleDetails({ roles }) {
  const { id } = useParams();
  const createModal = useCreateModal();
  const [isLoading, roleDetails] = useRoleById(id);

  const roleName = roleDetails?.name;

  const handleEditRole = useCallback(() => {
    createModal({
      content: (onClose) => (
        <AddUpdateRole type="edit" prevData={roleDetails} onClose={onClose} />
      ),
    });
  }, [createModal, roleDetails]);

  const handleDeleteRole = useCallback(() => {
    createModal({
      content: (onClose) => (
        <DeleteRole prevData={roleDetails} roles={roles} onClose={onClose} />
      ),
    });
  }, [createModal, roleDetails, roles]);

  return (
    <div className="flex-grow pl-4">
      {isLoading ? (
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-50 h-24 w-24" />
          <div className="flex-1 space-y-6 py-1">
            <div className="h-8 bg-gray-50 rounded" />
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-8 bg-gray-50 rounded col-span-2" />
                <div className="h-8 bg-gray-50 rounded col-span-1" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-4 pl-6 flex justify-between  border-b border-gray-50 ">
          <figure className="flex gap-8">
            <ThumbIcon
              className="h-24 w-24 rounded-full"
              initialsClassName="text-5xl "
              alt={roleName}
            />
            <figcaption className="pt-2">
              <h3 className="text-2xl capitalize font-semibold text-gray-700 tracking-wider">
                {roleName || '--'}
              </h3>
              <p className="font-[400] text-gray-700 tracking-wider uppercase">
                {roleDetails?.code?.replace(/_/g, ' ') || '--'}
              </p>
            </figcaption>
          </figure>
          <div className="flex gap-2 items-start">
            {aclHelper(
              'role',
              'u',
              <AsyncButton
                disabled={isLoading}
                onClick={handleEditRole}
                className="!capitalize "
                color="regular-with-border-gray"
              >
                <div className="flex gap-2 items-center">
                  <BsPencil />
                  <span>edit</span>
                </div>
              </AsyncButton>,
            )}

            {aclHelper(
              'role',
              'd',
              <AsyncButton
                disabled={isLoading}
                onClick={handleDeleteRole}
                className="!capitalize "
                color="regular-with-border-gray"
              >
                <div className="flex gap-2 items-center">
                  <FiTrash2 />
                  <span>delete</span>
                </div>
              </AsyncButton>,
            )}
          </div>
        </div>
      )}

      <Permissions roleId={id} />
    </div>
  );
}

RoleDetails.propTypes = {
  roles: PropTypes.instanceOf(Array).isRequired,
};

export default memo(RoleDetails);
